import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { detectNetwork } from '@/lib/payments/paymentAccount.format';
import type {
  BillingAddressValues,
  CardNetwork,
  PaymentAccountFormValues,
  PaymentTab,
} from '@/lib/payments/paymentAccount.types';

// Matches the abbreviations NetworkChip/paymentApi already use elsewhere in the app (VISA/MC/AMEX/DISC).
const NETWORK_LABEL: Record<CardNetwork, string> = {
  visa: 'VISA',
  mastercard: 'MC',
  amex: 'AMEX',
  discover: 'DISC',
  unknown: 'Card',
};

const last4 = (value: string) => value.replace(/\D/g, '').slice(-4);
const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

// Receipt-style label · value line.
function ReviewLine({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

interface PaymentReviewScreenProps {
  activeTab: PaymentTab;
  autoPayAmount: number | null;
  autoPayPlan: string | null;
  billing: BillingAddressValues;
}

/**
 * Screen 2: receipt-style confirmation of the entered account + AutoPay + billing. The
 * orchestrator owns the footer actions (Make changes / Confirm) so they sit in the sticky footer.
 */
export function PaymentReviewScreen({ activeTab, autoPayAmount, autoPayPlan, billing }: PaymentReviewScreenProps) {
  const { t } = useTranslation('members');
  const { formatCurrency } = useCurrency();
  const { getValues } = useFormContext<PaymentAccountFormValues>();
  const values = getValues();
  const isCard = activeTab === 'credit-card';

  const yn = (flag: boolean) =>
    flag ? t('payments.addAccount.reviewScreen.yes') : t('payments.addAccount.reviewScreen.no');

  const makeDefault = isCard ? values.card.makeDefault : values.bank.makeDefault;
  const isHsa = isCard ? values.card.isHsa : values.bank.isHsa;
  const autoPayOn = isCard ? values.card.autoPay : values.bank.autoPay;
  const hasBilling = billing.name.trim() !== '';

  const headline = isCard
    ? `${NETWORK_LABEL[detectNetwork(values.card.cardNumber)]} ···· ${last4(values.card.cardNumber)}`
    : `${values.bank.bankName || t('payments.addAccount.reviewScreen.bankFallback')} ···· ${last4(values.bank.accountNumber)}`;

  const caption = isCard
    ? `${t('payments.addAccount.reviewScreen.expPrefix')} ${values.card.expiry}`
    : capitalize(values.bank.accountType);

  return (
    <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-border bg-card">
      {/* Receipt header */}
      <div className="flex flex-col items-center border-b border-dashed border-border px-6 pb-6 pt-7 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-tint-brand text-primary">
          <Icon name={isCard ? 'creditCard' : 'landmark'} size="xl" />
        </span>
        <div className="mt-3 text-sm text-muted-foreground">{t('payments.addAccount.reviewScreen.youAdding')}</div>
        <div className="mt-1 text-2xl font-extrabold tracking-tight text-foreground">{headline}</div>
        <div className="mt-1 text-sm text-muted-foreground">
          {caption}
          {makeDefault ? ` · ${t('payments.addAccount.reviewScreen.defaultMethodSuffix')}` : ''}
        </div>
      </div>

      <div className="px-6 py-5">
        {autoPayOn && (
          <div className="rounded-2xl bg-muted p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-foreground">
                {t('payments.addAccount.reviewScreen.recurringAutoPay')}
              </span>
              <span className="text-2xl font-extrabold tabular-nums text-primary">
                {autoPayAmount === null ? '—' : formatCurrency(autoPayAmount)}
              </span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{autoPayPlan ?? '—'}</div>
          </div>
        )}

        <dl className={cn('divide-y divide-border/70', autoPayOn && 'mt-4')}>
          <ReviewLine label={t('payments.addAccount.reviewScreen.hsa')} value={yn(isHsa)} />
          {!autoPayOn && (
            <ReviewLine
              label={t('payments.addAccount.reviewScreen.autoPay')}
              value={t('payments.addAccount.reviewScreen.off')}
            />
          )}
          <ReviewLine label={t('payments.addAccount.reviewScreen.setAsDefault')} value={yn(makeDefault)} />
          <ReviewLine
            label={t('payments.addAccount.reviewScreen.billing')}
            value={
              hasBilling ? (
                <span className="text-right leading-relaxed">
                  {billing.name}
                  <br />
                  {billing.line1}
                  {billing.line2 ? `, ${billing.line2}` : ''}
                  <br />
                  {billing.city ? `${billing.city}, ` : ''}
                  {billing.state} {billing.zip}
                </span>
              ) : (
                t('payments.addAccount.reviewScreen.billingNone')
              )
            }
          />
        </dl>
      </div>
    </div>
  );
}
